/**
 * Pack presentation vocabulary — the ONE translation table for pack status, pack
 * sub-status and the round CPM tone.
 *
 * CARD-INFO-TAB (P-A) D9: these seven names moved here verbatim from
 * LeadDetailsMonitoringTab.jsx:82-124, :159-163 when the Инфо tab's «Амбассадорские
 * пачки» rows became a second reader of the same vocabulary. Copying them into the
 * Инфо tab instead would have created a second translation table for a value derived
 * in one place on the backend (backend/src/services/packSubStatus.js) — the drift
 * shape this repo already pays for elsewhere. No colocated test: a pure translation
 * map, matching the lib/collaborationStatuses.js and lib/activeCollab.js precedent.
 */

/** Status → Badge hue, spec D4 (same mapping the Monitoring screen uses): open is the
 *  INFO hue, not purple — PM iron rule 4 reserves purple for the single primary CTA. */
export const STATUS_COLOR = { open: 'blue', pending: 'orange', closed: 'green' };

// ── PSMO D7/D8 — the pack's fourth register on the card ──────────────────────

/** `Pack.status` — «собирается ли эта пачка». The manager's write surface is the
 *  select in PackBlock; the three values are the whole enum (spec §1). */
export const PACK_STATUS_LABEL = { active: 'Активная', inactive: 'Неактивная', failed: 'Не прошёл тест' };

/** The FULL sub-status vocabulary — the card is the surface that carries all of it
 *  (D7 «FE rendering rule»); the monitoring list shows only the off-state tier. The
 *  value itself is derived once, in backend/src/services/packSubStatus.js — nothing
 *  here re-derives the ladder, this map only translates. */
export const SUB_STATUS_LABEL = {
  failed:   'не прошёл тест',
  inactive: 'неактивная',
  stopped:  'остановлена',
  waiting:  'ждём старта',
  closed:   'тест закрыт',
  idle:     'нет раунда',
};

/** Sub-status → Badge hue, same D4 logic as STATUS_COLOR: the live tier is INFO,
 *  waiting is the pending hue, a closed test is green, the off tier is neutral —
 *  except `failed`, the one value that is a verdict rather than a switch. */
export const SUB_STATUS_COLOR = {
  failed:   'red',
  inactive: 'neutral',
  stopped:  'neutral',
  waiting:  'orange',
  closed:   'green',
  idle:     'neutral',
};

/** `round_N` carries its number, so the vocabulary is a map plus one pattern. */
export function subStatusLabel (subStatus) {
  const m = /^round_(\d+)$/.exec(String(subStatus ?? ''));
  if (m) return `раунд ${m[1]}`;
  return SUB_STATUS_LABEL[subStatus] ?? null;
}

export function subStatusColor (subStatus) {
  if (String(subStatus ?? '').startsWith('round_')) return STATUS_COLOR.open;
  return SUB_STATUS_COLOR[subStatus] ?? 'neutral';
}

/** Threshold tone for a round CPM — wave-1 `CRM-правки-волна-1.md:184-186` (OQ-4,
 *  apply 14): `<0,5` green · `0,5…5,0` yellow · `>5` red. The boundary value 0,5 is
 *  YELLOW, which deliberately overrules the reference's `<= 0.5 → green`
 *  (`LeadCard.dc.html:1419`). The tone resolves to a DS status token in
 *  lead-card.css (F10) — no hue is written here. */
export function cpmTone (cpm) {
  if (cpm == null) return null;
  if (cpm < 0.5) return 'green';
  if (cpm <= 5) return 'yellow';
  return 'red';
}
